import React, { useState } from 'react';
import { Sliders, Activity, Play, TrendingUp } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type ControlAnalysisType = 'pid_stability' | 'step_response' | 'bode_plot' | 'root_locus';

const ControlsAnalysis: React.FC = () => {
    const [analysisType, setAnalysisType] = useState<ControlAnalysisType>('pid_stability');
    const [params, setParams] = useState<{ [key: string]: any }>({
        kp: 1.0, ki: 0.1, kd: 0.0,
        plant_num: "1",
        plant_den: "1, 10, 20"
    });
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    // Helper to parse comma-separated arrays
    const parseArray = (str: string) => {
        return str.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
    };

    const handleCalculate = async () => {
        setLoading(true);
        try {
            const apiParams = { ...params };
            // Convert string inputs to arrays for backend
            if (params.plant_num) apiParams.plant_num = parseArray(params.plant_num);
            if (params.plant_den) apiParams.plant_den = parseArray(params.plant_den);
            if (params.sys_num) apiParams.sys_num = parseArray(params.sys_num);
            if (params.sys_den) apiParams.sys_den = parseArray(params.sys_den);

            const payload = {
                analysis_type: analysisType,
                parameters: apiParams
            };
            const data = await authenticatedFetchJSON('/api/v1/lab/controls-analysis', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch {
            setResult({ error: "Analysis failed. Verify transfer function format." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Sliders className="text-purple-500" /> Control Systems
                </h2>
                <div className="flex gap-2">
                    {(['pid_stability', 'step_response', 'bode_plot', 'root_locus'] as ControlAnalysisType[]).map(t => (
                        <button
                            key={t}
                            onClick={() => { setAnalysisType(t); setResult(null); }}
                            className={`px-3 py-1 text-xs rounded border ${analysisType === t ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-background border-border text-text-secondary'
                                }`}
                        >
                            {t.replace('_', ' ').toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    {analysisType === 'pid_stability' && (
                        <>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="block text-xs font-medium mb-1">Kp (Proportional)</label>
                                    <input type="number" step="0.1" value={params.kp} onChange={e => setParams({ ...params, kp: parseFloat(e.target.value) })} className="w-full bg-background border border-border rounded p-2 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1">Ki (Integral)</label>
                                    <input type="number" step="0.1" value={params.ki} onChange={e => setParams({ ...params, ki: parseFloat(e.target.value) })} className="w-full bg-background border border-border rounded p-2 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium mb-1">Kd (Derivative)</label>
                                    <input type="number" step="0.01" value={params.kd} onChange={e => setParams({ ...params, kd: parseFloat(e.target.value) })} className="w-full bg-background border border-border rounded p-2 text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Plant Numerator (coeff array)</label>
                                <input type="text" value={params.plant_num} onChange={e => setParams({ ...params, plant_num: e.target.value })} className="w-full bg-background border border-border rounded p-2 text-sm font-mono" placeholder="1" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Plant Denominator (coeff array)</label>
                                <input type="text" value={params.plant_den} onChange={e => setParams({ ...params, plant_den: e.target.value })} className="w-full bg-background border border-border rounded p-2 text-sm font-mono" placeholder="1, 10, 20" />
                                <p className="text-[10px] text-text-secondary mt-1">Example: &quot;1, 10, 20&quot; = s^2 + 10s + 20</p>
                            </div>
                        </>
                    )}

                    <button onClick={handleCalculate} disabled={loading} className="w-full bg-primary text-white py-2 rounded flex justify-center items-center gap-2 hover:bg-primary/90 mt-4">
                        {loading ? <Activity className="animate-spin" /> : <Play size={16} />} Run Simulation
                    </button>
                </div>

                <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px] overflow-hidden">
                    <h3 className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">Simulation Results</h3>
                    {result ? (
                        <div className="space-y-3">
                            {result.is_stable !== undefined && (
                                <div className={`flex items-center gap-2 p-2 rounded ${result.is_stable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    <Activity size={16} />
                                    <span className="font-bold">{result.is_stable ? 'SYSTEM STABLE' : 'SYSTEM UNSTABLE'}</span>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                {Object.entries(result).map(([k, v]) => (
                                    k !== 'is_stable' && (
                                        <div key={k}>
                                            <span className="text-text-secondary block text-xs capitalize">{k.replace(/_/g, ' ')}</span>
                                            <span className="font-mono text-text-primary">{String(v)}</span>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary opacity-50">
                            <TrendingUp size={32} className="mb-2" />
                            <p className="text-xs">Configure Plant & Controller to simulate response.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ControlsAnalysis;
